import L, { type GridLayer, type TileErrorEvent } from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useMap } from "react-leaflet";
import { ZodError } from "zod";
import {
  type Feature,
  leafletLayer,
  type PickedFeature,
  PolygonSymbolizer,
  type PaintRule,
} from "protomaps-leaflet";
import { getFarmlandDetailAPI } from "@/features/map/api/getFarmlandDetail";
import { FarmlandDetailDrawer } from "@/features/map/components/farmland/FarmlandDetailDrawer";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type FarmlandDetail,
  type FarmlandFilterValues,
  type FarmlandTileProperties,
} from "@/features/map/types/farmland";
import { buildFarmlandFilterMatcher } from "@/features/map/utils/farmlandFilter";
import { PointCircleSymbolizer } from "@/features/map/utils/pointSymbolizer";

interface Props {
  visible: boolean;
  filter: FarmlandFilterValues;
}

// NOTE: protomaps-leaflet では現状、feature picking が queryTileFeaturesDebug 経由でのみ提供されている。
// 将来 picking API を差し替えるときに影響範囲を広げないため、この依存はこのコンポーネント内に閉じ込める。
type PickableGridLayer = GridLayer & {
  queryTileFeaturesDebug: (
    lng: number,
    lat: number,
    brushSize?: number
  ) => Map<string, PickedFeature[]>;
  rerenderTiles: () => void;
};

export const FARMLAND_LAYER_NAME = "農地";
export const FARMLAND_LAYER_LABEL = "農地";

const FARMLAND_POINT_DATA_LAYER_NAME = "farmland_point";
const FARMLAND_POLYGON_DATA_LAYER_NAME = "farmland_polygon";
export const FARMLAND_MIN_ZOOM = 16;
export const FARMLAND_MAX_ZOOM = 22;
const MIN_ZOOM = FARMLAND_MIN_ZOOM;
const MAX_ZOOM = FARMLAND_MAX_ZOOM;
const MAX_DATA_ZOOM = 18;
const FARMLAND_TILE_ERROR_MESSAGE = "farmland tile load failed";
const FEATURE_PICK_BRUSH_SIZE = 16;
const TILE_ERROR_CAPTURE_INTERVAL_MS = 60_000;
const MAX_DETAIL_CACHE_SIZE = 100;
const DAICHO_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
// Leaflet Drawは図形確定直後に通常clickも流すため、農地詳細として拾わないよう短時間抑止する。
const DRAW_STOP_CLICK_SUPPRESSION_MS = 500;
const tileHost = process.env.VITE_COMMON_TILE_HOST;
const tileUrl = tileHost ? `${tileHost}/farmland/farmland.pmtiles` : null;
let hasCapturedMissingTileHost = false;
let hasCapturedInvalidDaichoId = false;

const POLYGON_FILL_COLOR = "#7fbf7b";
const POLYGON_OPACITY = 0.25;
const POLYGON_STROKE_COLOR = "#2f7d32";
const POLYGON_STROKE_WIDTH = 1;

const POINT_RADIUS = 4;
const POINT_STROKE_WIDTH = 1;
const POINT_FILL_COLOR = "#2f7d32";
const POINT_STROKE_COLOR = "#ffffff";
const POINT_OPACITY = 0.8;

const captureInvalidDaichoId = (daichoId: string): void => {
  if (hasCapturedInvalidDaichoId) return;

  hasCapturedInvalidDaichoId = true;
  Sentry.captureMessage("Invalid farmland daicho_id", {
    level: "warning",
    tags: {
      layer: "farmland",
    },
    extra: {
      daichoId,
      daichoIdLength: daichoId.length,
    },
  });
};

const cacheDetail = (
  detailCache: Map<string, FarmlandDetail>,
  daichoId: string,
  detail: FarmlandDetail
): void => {
  detailCache.delete(daichoId);
  if (detailCache.size >= MAX_DETAIL_CACHE_SIZE) {
    const oldestEntry = detailCache.keys().next();
    if (!oldestEntry.done) {
      detailCache.delete(oldestEntry.value);
    }
  }
  detailCache.set(daichoId, detail);
};

const buildPaintRules = (
  matchesFilter: (
    properties: FarmlandTileProperties | null | undefined
  ) => boolean
): PaintRule[] => [
  {
    dataLayer: FARMLAND_POLYGON_DATA_LAYER_NAME,
    symbolizer: new PolygonSymbolizer({
      fill: POLYGON_FILL_COLOR,
      opacity: POLYGON_OPACITY,
      stroke: POLYGON_STROKE_COLOR,
      width: POLYGON_STROKE_WIDTH,
    }),
    minzoom: MIN_ZOOM,
    maxzoom: MAX_ZOOM,
    filter: (_zoom: number, feature: Feature) =>
      matchesFilter(feature.props as FarmlandTileProperties),
  },
  {
    dataLayer: FARMLAND_POINT_DATA_LAYER_NAME,
    symbolizer: new PointCircleSymbolizer({
      radius: POINT_RADIUS,
      width: POINT_STROKE_WIDTH,
      fill: POINT_FILL_COLOR,
      stroke: POINT_STROKE_COLOR,
      opacity: POINT_OPACITY,
    }),
    minzoom: MIN_ZOOM,
    maxzoom: MAX_ZOOM,
    filter: (_zoom: number, feature: Feature) =>
      matchesFilter(feature.props as FarmlandTileProperties),
  },
];

const getDaichoId = (
  properties: FarmlandTileProperties | null | undefined
): string | null => {
  if (
    !properties ||
    properties.daicho_id === null ||
    properties.daicho_id === undefined
  ) {
    return null;
  }
  const daichoId = String(properties.daicho_id).trim();
  if (!daichoId) return null;
  if (!DAICHO_ID_PATTERN.test(daichoId)) {
    captureInvalidDaichoId(daichoId);
    return null;
  }
  return daichoId;
};

const findPickedFarmlandPointFeature = (
  featuresBySourceName: Map<string, PickedFeature[]>,
  matchesFilter: (
    properties: FarmlandTileProperties | null | undefined
  ) => boolean
): PickedFeature | null => {
  const pickedFeatures = Array.from(featuresBySourceName.values()).flat();
  return (
    pickedFeatures.find((pickedFeature) => {
      if (pickedFeature.layerName !== FARMLAND_POINT_DATA_LAYER_NAME) {
        return false;
      }

      return matchesFilter(
        pickedFeature.feature?.props as FarmlandTileProperties | undefined
      );
    }) ?? null
  );
};

const isLeafletControlEventTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return target.closest(".leaflet-control") !== null;
};

const isLeafletDrawnShapeEventTarget = (
  target: EventTarget | null
): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return target.closest(".leaflet-drawn-shapes-pane") !== null;
};

/**
 * 農地PMTilesを表示し、クリックした公開農地の詳細を取得するレイヤー。
 *
 * タイルは公開データとして扱い、filterは認可ではなく表示上の絞り込みだけを担う。
 */
export const FarmlandLayer: React.FC<Props> = ({ visible, filter }) => {
  const map = useMap();
  const { apiClient } = useApiClient();
  const [layer, setLayer] = useState<PickableGridLayer | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<FarmlandDetail | null>(
    null
  );
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const lastCapturedTileErrorAtRef = useRef<number | null>(null);
  const detailCacheRef = useRef<Map<string, FarmlandDetail>>(new Map());
  const detailAbortControllerRef = useRef<AbortController | null>(null);
  const latestDetailRequestIdRef = useRef(0);
  const isDrawingRef = useRef(false);
  const suppressDrawClickUntilRef = useRef(0);
  const matchesFilter = useMemo(
    () => buildFarmlandFilterMatcher(filter),
    [filter]
  );
  const matchesFilterRef = useRef(matchesFilter);
  // paintRulesをfilter変更ごとに再生成すると、protomaps-leafletがタイルを作り直す。
  // 参照は固定し、最新条件はmatchesFilterRef経由で読ませ、filter変更時はrerenderTiles()だけ実行する。
  const paintRules = useMemo(
    () => buildPaintRules((properties) => matchesFilterRef.current(properties)),
    []
  );

  const abortDetailRequest = useCallback((): void => {
    detailAbortControllerRef.current?.abort();
    detailAbortControllerRef.current = null;
    latestDetailRequestIdRef.current += 1;
  }, []);

  const handleDetailDrawerClose = useCallback((): void => {
    setDetailDrawerOpen(false);
    setLoadingDetail(false);
    abortDetailRequest();
  }, [abortDetailRequest]);

  const handleTileError = useCallback((event: TileErrorEvent): void => {
    const now = Date.now();
    if (
      lastCapturedTileErrorAtRef.current !== null &&
      now - lastCapturedTileErrorAtRef.current < TILE_ERROR_CAPTURE_INTERVAL_MS
    ) {
      return;
    }

    lastCapturedTileErrorAtRef.current = now;
    Sentry.captureException(
      event.error ?? new Error(FARMLAND_TILE_ERROR_MESSAGE),
      {
        tags: {
          layer: "farmland",
        },
        extra: {
          url: tileUrl,
          coords: {
            x: event.coords.x,
            y: event.coords.y,
            z: event.coords.z,
          },
        },
      }
    );
  }, []);

  useEffect(() => {
    if (tileUrl !== null || hasCapturedMissingTileHost) return;

    hasCapturedMissingTileHost = true;
    Sentry.captureMessage("VITE_COMMON_TILE_HOST is not configured", {
      level: "warning",
      tags: {
        layer: "farmland",
      },
    });
  }, []);

  useEffect(() => {
    matchesFilterRef.current = matchesFilter;
    if (!visible) return;

    layer?.rerenderTiles();
  }, [layer, matchesFilter, visible]);

  const fetchDetail = useCallback(
    async (daichoId: string): Promise<void> => {
      setDetailDrawerOpen(true);
      setDetailError(false);

      const cachedDetail = detailCacheRef.current.get(daichoId);
      if (cachedDetail) {
        detailAbortControllerRef.current?.abort();
        detailAbortControllerRef.current = null;
        cacheDetail(detailCacheRef.current, daichoId, cachedDetail);
        setSelectedDetail(cachedDetail);
        setLoadingDetail(false);
        return;
      }

      detailAbortControllerRef.current?.abort();
      const requestId = latestDetailRequestIdRef.current + 1;
      latestDetailRequestIdRef.current = requestId;
      const abortController = new AbortController();
      detailAbortControllerRef.current = abortController;
      setSelectedDetail(null);
      setLoadingDetail(true);

      try {
        const detail = await getFarmlandDetailAPI(
          apiClient,
          daichoId,
          abortController.signal
        );
        if (latestDetailRequestIdRef.current !== requestId) return;

        cacheDetail(detailCacheRef.current, daichoId, detail);
        setSelectedDetail(detail);
      } catch (error) {
        if (
          abortController.signal.aborted ||
          latestDetailRequestIdRef.current !== requestId
        ) {
          return;
        }
        setSelectedDetail(null);
        setDetailError(true);
        toast.error("農地詳細の取得に失敗しました。");
        Sentry.captureException(error, {
          tags: {
            layer: "farmland",
            action: "get_detail",
          },
          extra: {
            daichoId,
            zodIssues: error instanceof ZodError ? error.issues : undefined,
          },
        });
      } finally {
        if (latestDetailRequestIdRef.current === requestId) {
          setLoadingDetail(false);
        }
        if (detailAbortControllerRef.current === abortController) {
          detailAbortControllerRef.current = null;
        }
      }
    },
    [apiClient]
  );

  useEffect(() => {
    if (!map || tileUrl === null) return;

    const createdLayer = leafletLayer({
      url: tileUrl,
      paintRules,
      maxDataZoom: MAX_DATA_ZOOM,
    }) as PickableGridLayer;

    createdLayer.on("tileerror", handleTileError);
    createdLayer.setZIndex(LAYER_Z_INDEX.FARMLAND);
    setLayer(createdLayer);

    return () => {
      createdLayer.off("tileerror", handleTileError);
      if (map.hasLayer(createdLayer)) {
        map.removeLayer(createdLayer);
      }
      setLayer((currentLayer) =>
        currentLayer === createdLayer ? null : currentLayer
      );
    };
  }, [handleTileError, map, paintRules]);

  useEffect(() => {
    if (!map) return;

    const handleDrawStart = (): void => {
      isDrawingRef.current = true;
    };

    const handleDrawStop = (): void => {
      isDrawingRef.current = false;
      suppressDrawClickUntilRef.current =
        Date.now() + DRAW_STOP_CLICK_SUPPRESSION_MS;
    };

    map.on(L.Draw.Event.DRAWSTART, handleDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, handleDrawStop);

    return () => {
      map.off(L.Draw.Event.DRAWSTART, handleDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, handleDrawStop);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !layer) return;

    if (visible) {
      if (!map.hasLayer(layer)) {
        layer.addTo(map);
      }
      return;
    }

    if (map.hasLayer(layer)) {
      map.removeLayer(layer);
    }
  }, [layer, map, visible]);

  useEffect(() => {
    if (!map || !layer || !visible) return;

    const handleContainerClick = (event: MouseEvent): void => {
      if (event.defaultPrevented) return;
      if (isLeafletControlEventTarget(event.target)) return;
      if (isLeafletDrawnShapeEventTarget(event.target)) return;
      // Leaflet Drawの図形確定クリックは、農地詳細ではなく範囲選択操作として扱う。
      if (
        isDrawingRef.current ||
        Date.now() < suppressDrawClickUntilRef.current
      ) {
        return;
      }
      if (map.getZoom() < MIN_ZOOM) return;

      const point = map.mouseEventToContainerPoint(event);
      const latlng = map.containerPointToLatLng(point);
      const featuresBySourceName = layer.queryTileFeaturesDebug(
        latlng.lng,
        latlng.lat,
        FEATURE_PICK_BRUSH_SIZE
      );
      const pickedFeature = findPickedFarmlandPointFeature(
        featuresBySourceName,
        matchesFilterRef.current
      );
      if (!pickedFeature) return;

      const daichoId = getDaichoId(
        pickedFeature.feature?.props as FarmlandTileProperties | undefined
      );
      if (!daichoId) return;

      // 農地詳細を優先し、同じクリックで地番ポップアップなど後続の地図クリック処理を走らせない。
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      void fetchDetail(daichoId);
    };

    const mapContainer = map.getContainer();
    mapContainer.addEventListener("click", handleContainerClick, true);

    return () => {
      mapContainer.removeEventListener("click", handleContainerClick, true);
    };
  }, [fetchDetail, layer, map, visible]);

  useEffect(() => {
    if (!visible) {
      setDetailDrawerOpen(false);
      setSelectedDetail(null);
      setDetailError(false);
      setLoadingDetail(false);
      abortDetailRequest();
    }
  }, [abortDetailRequest, visible]);

  useEffect(() => {
    return () => {
      detailAbortControllerRef.current?.abort();
    };
  }, []);

  return (
    <FarmlandDetailDrawer
      open={detailDrawerOpen}
      detail={selectedDetail}
      loading={loadingDetail}
      error={detailError}
      onClose={handleDetailDrawerClose}
    />
  );
};
